/* eslint-disable no-shadow */
import DatePicker from 'antd/lib/date-picker';
import ConfigProvider from 'antd/lib/config-provider';
import { useEffect, useState } from 'react';
import locale from 'antd/locale/pt_BR';
import dayjs from 'dayjs';
import { ClbIcon, ClbTooltip } from '@celebration/react';
import { IRangePickerProps } from './IRangePickerProps';
import { ReactComponent as SpanIcon } from '../../assets/svg/spam.svg';
import { isNotEmpty } from '../../../utils/string-utils';

function RangePicker({
    id,
    title,
    startDate,
    startDateLabel,
    startDateNote,
    startDateError,
    endDate,
    endDateLabel,
    endDateNote,
    endDateError,
    minDate,
    canChooseSameDate,
    onChangeStartDate,
    onChangeEndDate,
    daysRangeLimit,
    getPopupContainer,
}: Readonly<IRangePickerProps>): JSX.Element {
    const [currentStartDate, setCurrentStartDate] = useState(startDate);
    const [currentEndDate, setCurrentEndDate] = useState(endDate);

    useEffect(() => {
        setCurrentStartDate(startDate);
    }, [startDate]);

    useEffect(() => {
        setCurrentEndDate(endDate);
    }, [endDate]);

    const minDateStart = minDate ? dayjs(minDate).startOf('day') : null;

    const isBeforeMinDate = (date: any) =>
        minDateStart !== null && date < minDateStart;

    const disabledStartDate = (date: any) => {
        if (!date) {
            return false;
        }

        if (isBeforeMinDate(date)) {
            return true;
        }

        if (!currentEndDate) {
            return false;
        }

        if (
            daysRangeLimit !== undefined &&
            date < dayjs(currentEndDate).subtract(daysRangeLimit, 'day')
        ) {
            return true;
        }

        return canChooseSameDate
            ? date > dayjs(currentEndDate)
            : date >= dayjs(currentEndDate);
    };

    const disabledEndDate = (date: any) => {
        if (!date) {
            return false;
        }

        if (isBeforeMinDate(date)) {
            return true;
        }

        if (!currentStartDate) {
            return false;
        }

        if (
            daysRangeLimit !== undefined &&
            date > dayjs(currentStartDate).add(daysRangeLimit, 'day')
        ) {
            return true;
        }

        return canChooseSameDate
            ? date < dayjs(currentStartDate)
            : date <= dayjs(currentStartDate);
    };

    const onStartChange = (value: any) => {
        const date = (value as dayjs.Dayjs)?.toDate();
        setCurrentStartDate(date);
        onChangeStartDate(date);
    };

    const onEndChange = (value: any) => {
        const date = (value as dayjs.Dayjs)?.toDate();
        setCurrentEndDate(date);
        onChangeEndDate(date);
    };

    const renderTooltip = (id: string, description: string | undefined) => {
        return (
            description && (
                <ClbTooltip id={id} position="right" description={description}>
                    <ClbIcon aria-labelledby={id} icon="info" size="sm" />
                </ClbTooltip>
            )
        );
    };

    const renderErrorMessage = (errorMessage: string | undefined) => {
        return (
            isNotEmpty(errorMessage) && (
                <span className="date-picker-input-error">
                    <SpanIcon />
                    {errorMessage}
                </span>
            )
        );
    };

    return (
        <ConfigProvider
            locale={locale}
            theme={{
                token: {
                    colorPrimary: `rgb(75, 166, 143)`,
                },
            }}
        >
            <div>{title && <h3>{title}</h3>}</div>
            <div className="range-date" id={id} data-testid={id}>
                <div className="range-date-input">
                    <span className="range-date-label">
                        {startDateLabel}
                        {renderTooltip(
                            'range-start-date-note-tooltip',
                            startDateNote,
                        )}
                    </span>
                    <DatePicker
                        disabledDate={disabledStartDate}
                        format="DD/MM/YYYY"
                        showNow={false}
                        value={
                            currentStartDate ? dayjs(currentStartDate) : null
                        }
                        placeholder="00/00/0000"
                        onChange={onStartChange}
                        className="date-picker-input"
                        data-testid="start-date"
                        {...(minDate && {
                            minDate: dayjs(minDate),
                        })}
                        status={isNotEmpty(startDateError) ? 'error' : ''}
                        {...(getPopupContainer && { getPopupContainer })}
                    />
                    {renderErrorMessage(startDateError)}
                </div>
                <div className="range-date-input">
                    <span className="range-date-label">
                        {endDateLabel}
                        {renderTooltip(
                            'range-end-date-note-tooltip',
                            endDateNote,
                        )}
                    </span>
                    <DatePicker
                        disabledDate={disabledEndDate}
                        format="DD/MM/YYYY"
                        showNow={false}
                        value={currentEndDate ? dayjs(currentEndDate) : null}
                        placeholder="00/00/0000"
                        onChange={onEndChange}
                        className="date-picker-input"
                        data-testid="end-date"
                        status={isNotEmpty(endDateError) ? 'error' : ''}
                        {...(getPopupContainer && { getPopupContainer })}
                    />
                    {renderErrorMessage(endDateError)}
                </div>
            </div>
        </ConfigProvider>
    );
}

export default RangePicker;
